import CustomLoader from "@/components/CustomLoader/customLoader.component";
import TaskPicker from "@/components/TaskPicker/taskPicker.component";
import { updateTimer } from "@/models/timers.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shadcn/components/ui/dialog";
import { Input } from "@/shadcn/components/ui/input";
import { OrganizedTimersEvent } from "@/types";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils";
import React, { useContext, useEffect, useRef, useState } from "react";

type EditTimerDialogProps = {
  timerToEdit: OrganizedTimersEvent;
  isOpen: boolean;
  onClose: () => void;
};

const EditTimerDialog: React.FC<EditTimerDialogProps> = ({
  timerToEdit,
  isOpen,
  onClose,
}) => {
  const {
    fetchPageTimers,
    fetchEveryoneTimers,
    initialGetUserTasks,
    getPrecacheTasks,
  } = useContext(AppContext);
  const [newTimer, setNewTimer] = useState({
    name: timerToEdit.timer.name || "",
    task_id: timerToEdit.timer.task_id || null,
    events: [],
  });
  const [creationState, setCreationState] = useState<
    "creation" | "submitting" | "success" | "failed"
  >("creation");
  const controllerRef = useRef(new AbortController());

  useEffect(() => {
    console.log(newTimer);
  }, [newTimer]);

  function handleEditField(field: keyof typeof newTimer, value: string) {
    setNewTimer((prev) => ({ ...prev, [field]: value }));
  }

  function handleTaskID(id: number) {
    setNewTimer((prev) => ({
      ...prev,
      task_id: prev.task_id === id ? null : id,
    }));
  }

  useEffect(() => {
    console.log("timerToEdit", timerToEdit);
  }, [timerToEdit]);

  async function handleSubmit() {
    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;

    setCreationState("submitting");

    const response = await updateTimer({ timer: timerToEdit });

    if (!response) {
      genericFailureToast("Failed to update timer");
      setCreationState("failed");
      return;
    }

    console.log("Timer updated successfully");
    setCreationState("success");
    genericSuccessToast("Timer updated successfully");
    fetchPageTimers(signal, fetchEveryoneTimers);
    await closeDialog();
    await initialGetUserTasks({ force: true });
    await getPrecacheTasks(false, true);
  }

  async function closeDialog() {
    setNewTimer({
      name: "",
      task_id: null,
      events: [],
    });
    onClose();
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] !rounded-sm">
        <DialogHeader>
          <DialogTitle>Edit Timer</DialogTitle>
        </DialogHeader>
        {creationState === "creation" ? (
          <>
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <div className="grid grid-rows-2 gap-2 items-center">
                <label
                  className="justify-self-start mr-4"
                  htmlFor="edit-timer-title"
                >
                  Name
                </label>
                <label
                  className="justify-self-start mr-4"
                  htmlFor="edit-timer-task"
                >
                  Task
                  <span className="ms-1 text-xs text-gray-500">(optional)</span>
                </label>
              </div>
              <div className="grid grid-rows-2 gap-2">
                <Input
                  id="edit-title"
                  value={newTimer.name}
                  onChange={(e) => handleEditField("name", e.target.value)}
                  className="truncate"
                  maxLength={255}
                />
                <TaskPicker
                  type={"User"}
                  taskID={newTimer.task_id}
                  handleFieldTask={(id: number) => handleTaskID(id)}
                  side="right"
                  align="start"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={!newTimer.name}
              >
                Edit Timer
              </Button>
            </DialogFooter>
          </>
        ) : creationState === "submitting" ? (
          <CustomLoader size="sm" />
        ) : creationState === "success" ? (
          <DialogDescription>Success</DialogDescription>
        ) : (
          <DialogDescription>Failed</DialogDescription>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditTimerDialog;
